<script setup lang="ts">
/**
 * #957: the Actions section of the connection editor, beside Fills slots. One row for each action the enterprise's
 * tasks declare under a slot this connection fills (once each, with its description), one for each action the
 * connection's saved map already names (an Express connection's seeded map included), and a free row for one more.
 *
 * Each row picks one of the tools the server serves (read from the connection's catalog) and may pin it (`field=value`)
 * for a tool that does several things; the target saved is `tool` or `tool#pin`. When the served tools cannot be read
 * the picker becomes a typed tool-name field, so a run blocked on an unmapped action can still be fixed.
 *
 * Every action, tool and pin on screen comes from the API: the tasks' declarations, the connection's map and the
 * server's catalog. The section names none of its own. `collect()` is what the editor's save sends: the whole map, with
 * `''` for a saved row that was cleared, or null when a row breaks the shared rule (the row then says why).
 */
import { MAX_SLOT_ACTIONS, SLOT_ACTION_TARGET, TOOL_SLOT_ACTION } from '@zerohuman/shared'
import {
    Button,
    Input,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    ZhFieldError,
    ZhFormField,
    ZhSectionHeading,
} from '@zerohuman/ui'
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { api, type TaskRow } from '@/api'

const props = defineProps<{
    /** The connection being edited: its catalog is what the pickers offer. */
    bindingId: string
    /** The slots the connection fills: those typed in Fills slots, and its own tool type when a task declares it. */
    slots: readonly string[]
    /** The enterprise's tasks, whose current versions declare the actions. */
    tasks: readonly TaskRow[]
    /** The connection's saved action map. */
    saved: Readonly<Record<string, string>>
    /** Scroll the section into view once it is drawn (the editor was opened at its Actions section). */
    reveal?: boolean
}>()

type Entry = { tool: string; pin: string }
type Problem = { message: string; fix: string }

/** The tool and the pin a saved target is made of: the pin is everything after the first `#`. */
function splitTarget(target: string): Entry {
    const hash = target.indexOf('#')
    return hash < 0 ? { tool: target, pin: '' } : { tool: target.slice(0, hash), pin: target.slice(hash + 1) }
}

/** The actions declared under the filled slots, by name, each once: the first description given wins. */
const declared = computed(() => {
    const filled = new Set(props.slots)
    const found = new Map<string, string>()
    for (const task of props.tasks) {
        for (const slot of task.currentVersion?.toolSlots ?? []) {
            if (!filled.has(slot.key)) continue
            for (const action of slot.actions ?? []) {
                const description = action.description?.trim() ?? ''
                if (!found.get(action.name)) found.set(action.name, description)
            }
        }
    }
    return found
})

/** The rows' action names: the declared ones in the order found, then the ones only the saved map names. */
const rowNames = computed(() => [
    ...declared.value.keys(),
    ...Object.keys(props.saved).filter(name => !declared.value.has(name)),
])

const entries = ref<Record<string, Entry>>(
    Object.fromEntries(Object.entries(props.saved).map(([name, target]) => [name, splitTarget(target)])),
)
watch(
    rowNames,
    names => {
        for (const name of names) {
            if (!entries.value[name]) entries.value[name] = { tool: '', pin: '' }
        }
    },
    { immediate: true },
)

const rows = computed(() =>
    rowNames.value.flatMap(name => {
        const entry = entries.value[name]
        return entry ? [{ name, description: declared.value.get(name) ?? '', entry }] : []
    }),
)

const free = ref<Entry & { name: string }>({ name: '', tool: '', pin: '' })

/** The served tool names; null while they load or when they could not be read. */
const catalog = ref<string[] | null>(null)
/** Whether the served tools could not be read (or the server lists none): each row then takes a typed name. */
const typed = ref(false)
/** Whether the served tools are still being read: the pickers say so rather than look empty. */
const loading = ref(true)
/** What the typed fields stand in for: the list could not be read, or the server lists no tools. */
const typedNote = computed(() =>
    catalog.value === null
        ? "This server's tools could not be listed, so type each tool's name as the server lists it."
        : 'This server lists no tools, so type each tool’s name as the server names it.',
)

async function loadCatalog(): Promise<void> {
    try {
        const served = (await api.toolCatalog(props.bindingId)).map(tool => tool.name)
        catalog.value = served
        typed.value = served.length === 0
    } catch {
        catalog.value = null
        typed.value = true
    } finally {
        loading.value = false
    }
}

/** What a picker offers: the served tools, and the row's own tool when the server does not list it (still shown). */
function optionsFor(current: string): string[] {
    const served = catalog.value ?? []
    return current && !served.includes(current) ? [current, ...served] : served
}

const errors = ref<Record<string, Problem>>({})
watch([entries, free], () => (errors.value = {}), { deep: true })

/** The target a row saves: `tool`, `tool#pin`, or a typed one that carries its own `#pin` as typed. */
function targetOf(entry: Entry): string {
    const tool = entry.tool.trim()
    const pin = entry.pin.trim().replace(/^#/, '')
    if (!tool) return ''
    return tool.includes('#') || !pin ? tool : `${tool}#${pin}`
}

/** Why a row's target cannot be saved, by the shared rule the API applies, or null when it can. */
function targetProblem(entry: Entry): Problem | null {
    const target = targetOf(entry)
    if (!target) {
        return entry.pin.trim()
            ? {
                  message: 'The second box says which one, but there is no tool.',
                  fix: 'Choose the tool it is for, or empty the second box.',
              }
            : null
    }
    if (!SLOT_ACTION_TARGET.test(target)) {
        return {
            message: `"${target}" is not a tool this server can name.`,
            fix: typed.value
                ? 'Copy the tool’s name exactly as the server lists it, and take out any spaces from the second box.'
                : 'Choose the tool from the list, and take out any spaces from the second box.',
        }
    }
    return null
}

function freeProblem(name: string): Problem | null {
    if (!name) return { message: 'This action has no name.', fix: 'Name it the way tasks name it under the slot.' }
    if (!TOOL_SLOT_ACTION.test(name)) {
        return {
            message: `"${name}" is not an action name.`,
            fix: 'Copy the name exactly as the task’s Tool slots list it.',
        }
    }
    if (rowNames.value.includes(name)) {
        return { message: `"${name}" has a row of its own.`, fix: 'Map it on that row instead.' }
    }
    const problem = targetProblem(free.value)
    if (problem) return problem
    if (!targetOf(free.value))
        return { message: 'This action has no tool.', fix: 'Choose the tool it is on this server.' }
    return null
}

/**
 * The whole map the save sends: each row with a target, `''` for a saved row that was cleared, and the free row when
 * it names an action. Null when a row breaks the shared rule, and then that row says why.
 */
function collect(): Record<string, string> | null {
    const problems: Record<string, Problem> = {}
    const map: Record<string, string> = {}
    for (const row of rows.value) {
        const problem = targetProblem(row.entry)
        if (problem) {
            problems[row.name] = problem
            continue
        }
        const target = targetOf(row.entry)
        if (target) map[row.name] = target
        else if (row.name in props.saved) map[row.name] = ''
    }
    const freeName = free.value.name.trim()
    if (freeName || free.value.tool.trim() || free.value.pin.trim()) {
        const problem = freeProblem(freeName)
        if (problem) problems.free = problem
        else map[freeName] = targetOf(free.value)
    }
    const mapped = Object.values(map).filter(Boolean).length
    if (Object.keys(problems).length === 0 && mapped > MAX_SLOT_ACTIONS) {
        problems.section = {
            message: `This maps ${mapped} actions; a connection maps at most ${MAX_SLOT_ACTIONS}.`,
            fix: 'Clear the rows it does not need.',
        }
    }
    errors.value = problems
    return Object.keys(problems).length > 0 ? null : map
}

function clear(entry: Entry): void {
    entry.tool = ''
    entry.pin = ''
}

/** Empty the free row: it saves nothing then. */
function clearFree(): void {
    free.value = { name: '', tool: '', pin: '' }
}

defineExpose({ collect })

const section = ref<HTMLElement | null>(null)
onMounted(async () => {
    await loadCatalog()
    if (props.reveal) {
        await nextTick()
        section.value?.scrollIntoView?.({ block: 'start' })
    }
})

const errorId = (name: string) => `tool-binding-action-error-${name}`
</script>

<template>
	<section
		id="tool-binding-actions"
		ref="section"
		class="grid gap-3"
		aria-labelledby="tool-binding-actions-heading"
		data-testid="actions-section"
	>
		<ZhSectionHeading id="tool-binding-actions-heading" as="h3">Actions</ZhSectionHeading>
		<p class="text-muted-foreground text-sm">
			For each action a task needs from a slot this connection fills, choose the tool on this server that does it.
			Only if that tool does several things, say in the second box which one to use.
		</p>
		<p v-if="loading" class="text-muted-foreground text-sm" role="status" data-testid="actions-catalog-loading">
			Loading this server's tools…
		</p>
		<p v-if="typed" class="text-muted-foreground text-sm" data-testid="actions-catalog-note">
			{{ typedNote }}
		</p>
		<div v-for="row in rows" :key="row.name" class="grid gap-2" :data-testid="`action-row-${row.name}`">
			<ZhFormField
				:label="row.name"
				:help="row.description || 'No task declares it now; it is still saved on this connection.'"
				example="a tool from the list; the second box only when that tool does several things"
				:error-id="errors[row.name] ? errorId(row.name) : undefined"
				group
			>
				<template #default="{ describedBy }">
					<div class="flex flex-wrap gap-2">
						<Select v-if="!typed" v-model="row.entry.tool">
							<SelectTrigger
								class="min-w-0 flex-1"
								:aria-label="`Tool for ${row.name}`"
								:aria-describedby="describedBy"
							>
								<SelectValue :placeholder="loading ? 'Loading tools…' : 'Choose a tool'" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem v-for="tool in optionsFor(row.entry.tool)" :key="tool" :value="tool">
									{{ tool }}
								</SelectItem>
							</SelectContent>
						</Select>
						<Input
							v-else
							v-model="row.entry.tool"
							class="min-w-0 flex-1"
							placeholder="tool name"
							:aria-label="`Tool for ${row.name}`"
							:aria-describedby="describedBy"
							:data-testid="`action-tool-input-${row.name}`"
						/>
						<Input
							v-model="row.entry.pin"
							class="w-48"
							placeholder="which one (optional)"
							:aria-label="`Which one the tool for ${row.name} does (optional)`"
							:aria-describedby="describedBy"
							:data-testid="`action-pin-${row.name}`"
						/>
						<Button
							v-if="row.entry.tool || row.entry.pin"
							type="button"
							size="sm"
							variant="outline"
							:aria-label="`Clear ${row.name}`"
							:data-testid="`action-clear-${row.name}`"
							@click="clear(row.entry)"
						>
							Clear
						</Button>
					</div>
				</template>
				<template #error>
					<ZhFieldError
						v-if="errors[row.name]"
						:id="errorId(row.name)"
						v-bind="errors[row.name]"
						:data-testid="`action-error-${row.name}`"
					/>
				</template>
			</ZhFormField>
		</div>
		<div class="grid gap-2" data-testid="action-row-free">
			<ZhFormField
				label="Another action"
				help="An action no row above holds, named the way tasks name it under the slot, and its tool."
				example="lowercase words joined by dots, then the tool"
				:error-id="errors.free ? errorId('free') : undefined"
				group
			>
				<template #default="{ describedBy }">
					<div class="flex flex-wrap gap-2">
						<Input
							v-model="free.name"
							class="w-48"
							placeholder="action name"
							aria-label="Action name"
							:aria-describedby="describedBy"
							data-testid="action-free-name"
						/>
						<Select v-if="!typed" v-model="free.tool">
							<SelectTrigger class="min-w-0 flex-1" aria-label="Tool for another action" :aria-describedby="describedBy">
								<SelectValue :placeholder="loading ? 'Loading tools…' : 'Choose a tool'" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem v-for="tool in optionsFor(free.tool)" :key="tool" :value="tool">
									{{ tool }}
								</SelectItem>
							</SelectContent>
						</Select>
						<Input
							v-else
							v-model="free.tool"
							class="min-w-0 flex-1"
							placeholder="tool name"
							aria-label="Tool for another action"
							:aria-describedby="describedBy"
							data-testid="action-tool-input-free"
						/>
						<Input
							v-model="free.pin"
							class="w-48"
							placeholder="which one (optional)"
							aria-label="Which one the tool for another action does (optional)"
							:aria-describedby="describedBy"
							data-testid="action-pin-free"
						/>
						<Button
							v-if="free.name || free.tool || free.pin"
							type="button"
							size="sm"
							variant="outline"
							aria-label="Clear another action"
							data-testid="action-clear-free"
							@click="clearFree"
						>
							Clear
						</Button>
					</div>
				</template>
				<template #error>
					<ZhFieldError v-if="errors.free" :id="errorId('free')" v-bind="errors.free" data-testid="action-error-free" />
				</template>
			</ZhFormField>
		</div>
		<ZhFieldError
			v-if="errors.section"
			id="tool-binding-actions-error"
			v-bind="errors.section"
			data-testid="actions-error"
		/>
	</section>
</template>
